/**
 * Portal Settings hooks — admin builder at /settings/portal.
 *
 * Wire the relocated NC admin UI (app/settings/portal/**) to the NestJS
 * backend. Every mutation invalidates the appropriate query keys so the
 * settings panel + live preview stay in sync.
 *
 * Backend routes (src/modules/portal-settings/portal-settings.controller.ts):
 *   GET  /v1/portal-settings/public?workspaceId=X   — @Public() anon-safe subset
 *   GET  /v1/portal-settings                        — admin (workspace resolved server-side)
 *   PUT  /v1/portal-settings
 *   GET  /v1/portal-settings/company-default
 *   PUT  /v1/portal-settings/company-default
 *   GET  /v1/portal-themes
 *   GET  /v1/portal-themes/:id
 *   POST /v1/portal-themes
 *   PATCH /v1/portal-themes/:id
 *   DELETE /v1/portal-themes/:id
 */
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { portalAdminFetch } from '@/lib/portal/api';
import {
  COMPANY_TIER_READ_ONLY_HINT,
  useIsCompanyTier,
} from '@/lib/auth/company-tier';
import type {
  PortalSettings,
  PortalSettingsResolved,
  PortalSettingsWritePayload,
  PortalTheme,
  PortalFieldSources,
} from '@/lib/types/portal-settings';

// Re-export for any component that pulled these through the hook file.
export type {
  PortalSettings,
  PortalSettingsResolved,
  PortalSettingsWritePayload,
  PortalTheme,
  PortalFieldSources,
};

// ============================================================================
// Query Key Factory
// ============================================================================

export const portalSettingsKeys = {
  all: () => ['portal-settings'] as const,
  resolved: () => [...portalSettingsKeys.all(), 'resolved'] as const,
  companyDefault: () => [...portalSettingsKeys.all(), 'company-default'] as const,
  public: (workspaceId: string) =>
    ['portal-settings', 'public', workspaceId] as const,
};

export const portalThemesKeys = {
  all: () => ['portal-themes'] as const,
  list: () => [...portalThemesKeys.all(), 'list'] as const,
  detail: (id: string) => [...portalThemesKeys.all(), 'detail', id] as const,
};

// ============================================================================
// Portal Settings — public (anon-safe, keyed by workspaceId)
// ============================================================================

/**
 * Public portal settings for the employee-facing portal shell.
 * No SSO secrets, no admin-only toggles — safe for @Public() consumption.
 * Backend wraps the response in the same DTO shape as the admin endpoint
 * (settings + _fieldSources) so consumers can reuse the same type.
 */
export function usePublicPortalSettings(workspaceId: string) {
  return useQuery({
    queryKey: portalSettingsKeys.public(workspaceId),
    queryFn: () =>
      fetchApi<PortalSettingsResolved>(
        `/portal-settings/public?workspaceId=${encodeURIComponent(workspaceId)}`,
        { skipAuth: true },
      ),
    enabled: !!workspaceId,
  });
}

// ============================================================================
// Portal Settings — admin (current workspace resolved server-side)
// ============================================================================

export function usePortalSettings() {
  return useQuery({
    queryKey: portalSettingsKeys.resolved(),
    queryFn: () => portalAdminFetch<PortalSettingsResolved>('portal-settings'),
    placeholderData: keepPreviousData,
  });
}

export function useUpdatePortalSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: PortalSettingsWritePayload) =>
      portalAdminFetch<PortalSettingsResolved>('portal-settings', {
        method: 'PUT',
        body: JSON.stringify(dto),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalSettingsKeys.all() });
    },
  });
}

// ============================================================================
// Portal Settings — company default
// ============================================================================

export function usePortalSettingsCompanyDefault() {
  return useQuery({
    queryKey: portalSettingsKeys.companyDefault(),
    queryFn: () =>
      portalAdminFetch<PortalSettingsResolved>('portal-settings/company-default'),
    placeholderData: keepPreviousData,
  });
}

/**
 * COMPANY-TIER WRITE. The row this PUT/PATCH touches is inherited by every
 * workspace in the company, and since RC-I (2026-09-20) the API refuses it
 * from anyone below Company Owner / Company Admin. The READ side stays open
 * on purpose — a workspace must be able to see what it inherits — so the
 * form still loads for a Workspace Admin. Refusing HERE, in the one hook
 * every caller goes through, is what stops a doomed request and puts the
 * reason in front of the operator instead of a bare 403 toast.
 */
export function useUpdatePortalSettingsCompanyDefault() {
  const qc = useQueryClient();
  const isCompanyTier = useIsCompanyTier();
  return useMutation({
    mutationFn: (dto: PortalSettingsWritePayload) => {
      if (!isCompanyTier) {
        return Promise.reject(new Error(COMPANY_TIER_READ_ONLY_HINT));
      }
      return portalAdminFetch<PortalSettingsResolved>('portal-settings/company-default', {
        method: 'PUT',
        body: JSON.stringify(dto),
      });
    },
    onSuccess: () => {
      // Changing company defaults may cascade into every workspace that
      // inherits — invalidate the whole portal-settings tree.
      void qc.invalidateQueries({ queryKey: portalSettingsKeys.all() });
    },
  });
}

// ============================================================================
// Portal Themes — catalog CRUD
// ============================================================================

export function usePortalThemes() {
  return useQuery({
    queryKey: portalThemesKeys.list(),
    queryFn: () => portalAdminFetch<PortalTheme[]>('portal-themes'),
    placeholderData: keepPreviousData,
  });
}

/**
 * Public theme list — anon-safe. Used by the employee portal shell + login
 * page which resolve theme colors before auth.
 * Scoped by ?workspaceId=X; malformed/unknown workspace still returns the
 * global built-ins so the shell renders with defaults.
 */
export function usePublicPortalThemes(workspaceId: string) {
  // Public endpoint — anon, not scoped by admin mode; use a stable key.
  return useQuery({
    queryKey: ['portal-themes', 'public', workspaceId] as const,
    queryFn: () =>
      fetchApi<PortalTheme[]>(
        `/portal-themes/public?workspaceId=${encodeURIComponent(workspaceId)}`,
        { skipAuth: true },
      ),
    enabled: !!workspaceId,
  });
}

export function usePortalTheme(id: string) {
  return useQuery({
    queryKey: portalThemesKeys.detail(id),
    queryFn: () => portalAdminFetch<PortalTheme>(`portal-themes/${id}`),
    enabled: !!id,
  });
}

export function useCreatePortalTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: Partial<PortalTheme>) =>
      portalAdminFetch<PortalTheme>('portal-themes', {
        method: 'POST',
        body: JSON.stringify(dto),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalThemesKeys.list() });
    },
  });
}

export function useUpdatePortalTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<PortalTheme> }) =>
      portalAdminFetch<PortalTheme>(`portal-themes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(dto),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: portalThemesKeys.list() });
      void qc.invalidateQueries({
        queryKey: portalThemesKeys.detail(vars.id),
      });
    },
  });
}

export function useDeletePortalTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      portalAdminFetch<void>(`portal-themes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalThemesKeys.list() });
    },
  });
}
